








































































































































import { Component, Vue, Prop } from 'vue-property-decorator';
import { formatDigitsGrouping } from '@/modules/budget/monitoring/reports-constructor/common/utils/formattingUtils';
import { IApp21ExpenseRow, IApp21KrRow } from './app21InputForm';
import {
    blockNegativeFactKey,
    blockNegativeFactPaste,
    INDICATOR_PRECISION,
    IReasonOption,
    IRowValue,
    isReasonMissing,
    MONEY_PRECISION,
    parseFact,
    reasonKey,
    rowValues,
} from './app21Utils';

const I18N_PREFIX = 'modules.budget_execution_monitoring.reports.app21';
const NUMBERING_COLORS = ['green', 'red', 'purple', 'orange', 'blue'];

@Component({
    filters: {
        formatNumber(value: unknown): unknown {
            if (typeof value !== 'number' || !Number.isFinite(value)) {
                return value;
            }
            return formatDigitsGrouping(value.toFixed(1)).replace('.', ',');
        },
        // КР: без принудительного знака после запятой (100, -1), с округлением погрешности float
        formatPlain(value: unknown): unknown {
            if (typeof value !== 'number' || !Number.isFinite(value)) {
                return value;
            }
            const rounded = Math.round(value * 1e6) / 1e6;
            return formatDigitsGrouping(rounded).replace('.', ',');
        }
    }
})
export default class ApplicationForm21Expenses extends Vue {
    // данные принадлежат родителю (application21.vue); изменения — через события
    // строки budget_execution_application21 по программе и всем ППР
    @Prop({ default: () => [] }) private expenses!: IApp21ExpenseRow[];
    // строки budget_execution_application21_kr
    @Prop({ default: () => [] }) private kr!: IApp21KrRow[];
    // наименование БП на текущем языке
    @Prop({ default: '' }) private prgName!: string;
    @Prop({ default: () => [] }) private reasonOptions!: IReasonOption[];
    // все причины — для показа выбранной, даже если она перестала действовать
    @Prop({ default: () => [] }) private allReasonOptions!: IReasonOption[];
    @Prop({ default: false }) private editAccess!: boolean;
    // строки с ошибкой при последнем неудачном сохранении (ключ — reasonKey)
    @Prop({ default: () => ({}) }) private highlighted!: Record<string, boolean>;

    private readonly i18nPrefix = I18N_PREFIX;


    // итоговая строка по программе в целом
    private get totalRow(): IApp21ExpenseRow | null {
        return this.expenses.find(x => x.ppr === null) ?? null;
    }

    private get expenseValues(): IRowValue[] {
        return rowValues(
            this.prgName,
            this.$t(`${I18N_PREFIX}.thousand_tenge`) as string,
            this.totalRow?.plan ?? null,
            this.totalRow?.fact ?? null,
        );
    }

    // строки раздела 2 — КР БП; при смене языка пересчитываются без повторного запроса
    private get krRows(): { kr: IApp21KrRow; values: IRowValue[] }[] {
        return this.kr.map(kr => ({
            kr,
            values: rowValues(
                this.localized(kr.kr_name_ru, kr.kr_name_kk) || kr.kr_code,
                this.localized(kr.unit_name_ru, kr.unit_name_kk) || kr.unit_code || '-',
                kr.plan,
                kr.fact,
            ),
        }));
    }

    // значение по текущему языку; en и пустой kk — на ru
    private localized(ru: string | null, kk: string | null): string {
        return (this.$i18n.locale === 'kk' ? kk || ru : ru) || '';
    }

    private reasonOption(reasonId: number | null): IReasonOption | null {
        if (reasonId === null) return null;
        return this.allReasonOptions.find(x => x.key === reasonId) ?? null;
    }

    private get totalReason(): IReasonOption | null {
        return this.reasonOption(this.totalRow?.reason_id ?? null);
    }

    // подсветка — только у строк с ошибкой при последнем сохранении и пока ошибка не исправлена
    private get isTotalReasonHighlighted(): boolean {
        const row = this.totalRow;
        return !!row
            && !!this.highlighted[reasonKey('expenses', row.id)]
            && isReasonMissing(row.plan, row.fact, row.reason_id, MONEY_PRECISION);
    }

    private isKrReasonHighlighted(kr: IApp21KrRow): boolean {
        return !!this.highlighted[reasonKey('kr', kr.id)]
            && isReasonMissing(kr.plan, kr.fact, kr.reason_id, INDICATOR_PRECISION);
    }

    private get tableHeaders(): { value: string; key: string; width: string }[] {
        const columns: { key: string; width: string }[] = [
            { key: 'name',      width: '29%' },
            { key: 'unit',      width: '10%' },
            { key: 'plan',      width: '10%' },
            { key: 'fact',      width: '10%' },
            { key: 'deviation', width: '10%' },
            { key: 'percent',   width: '15.5%' },
            { key: 'reason',    width: '15.5%' },
        ];
        return columns.map(col => ({
            ...col,
            value: this.$t(`${I18N_PREFIX}.${col.key}`) as string,
        }));
    }

    // причина по БП в целом: родитель синхронизирует её с «Показателями» у БП без подпрограмм
    private onTotalReasonInput(option: IReasonOption | null) {
        if (!this.totalRow) return;
        this.$emit('expense-reason-change', this.totalRow, option?.key ?? null);
    }

    private onKrReasonInput(kr: IApp21KrRow, option: IReasonOption | null) {
        this.$emit('reason-change', kr, option?.key ?? null);
    }

    // пустое поле = факт не введён (null), отклонение и % не считаются
    private onKrFactInput(kr: IApp21KrRow, value: string) {
        this.$emit('fact-change', kr, parseFact(value));
    }

    // отрицательный факт не вводится
    private blockNegativeFactKey(event: KeyboardEvent) {
        blockNegativeFactKey(event);
    }

    private blockNegativeFactPaste(event: ClipboardEvent) {
        blockNegativeFactPaste(event);
    }

    // цвет ячейки 
    private numberingColor(idx: number): string {
        return NUMBERING_COLORS[idx % NUMBERING_COLORS.length];
    }

}
